/**
 * SIDEBAR_OPEN_WIDTH
 *
 * The Tailwind width class applied to all collapsible filter sidebars when
 * open. 319 px gives CalendarPicker (w-[280px]) enough room to render inside
 * a sidebar that has 16 px (p-4) of horizontal padding on each side:
 *   319 − 2×16 = 287 px  >  280 px  ✓
 *
 * Using a single constant ensures every filter sidebar is consistently wide
 * enough that the calendar dropdown never clips its "next month" navigation.
 *
 * Usage with ResponsiveFilterPanel:
 *   <ResponsiveFilterPanel openWidthClassName={SIDEBAR_OPEN_WIDTH} …>
 *
 * Usage with a hardcoded div (legacy pattern):
 *   <div className={`… ${isSidebarOpen ? SIDEBAR_OPEN_WIDTH : SIDEBAR_COLLAPSED_WIDTH}`}>
 */
export const SIDEBAR_OPEN_WIDTH = 'w-80';

/**
 * SIDEBAR_COLLAPSED_WIDTH
 *
 * The Tailwind width class applied to all collapsible filter sidebars when
 * collapsed (minimised). Shows only the filter icon/toggle affordance.
 * 68 px is wide enough for a centred icon (24 px) with comfortable padding.
 *
 * Usage with ResponsiveFilterPanel:
 *   <ResponsiveFilterPanel collapsedWidthClassName={SIDEBAR_COLLAPSED_WIDTH} …>
 *
 * Usage with a hardcoded div (legacy pattern):
 *   <div className={`… ${isSidebarOpen ? SIDEBAR_OPEN_WIDTH : SIDEBAR_COLLAPSED_WIDTH}`}>
 *
 * Usage as a standalone collapsed sidebar:
 *   <aside className={`${SIDEBAR_COLLAPSED_WIDTH} …`}>
 */
export const SIDEBAR_COLLAPSED_WIDTH = 'w-[68px]';

/**
 * FILTER_COLLAPSED_ICON_CONTAINER_HEIGHT
 *
 * The pixel height of the filter-icon container shown when a filter sidebar
 * is collapsed. Derived from the design spec:
 *   top/bottom padding: 22px  +  icon height: 20px  = 64px total
 *
 * The matching horizontal padding is 24px (left + right), making the
 * total container width 24 + 20 + 24 = 68px (matches SIDEBAR_COLLAPSED_WIDTH).
 *
 * Use this constant (via inline style) in every collapsed filter sidebar so
 * the height can be changed in one place.
 *
 * Usage:
 *   <div style={{ height: FILTER_COLLAPSED_ICON_CONTAINER_HEIGHT }} className="…px-6 py-[22px]…">
 */
export const FILTER_COLLAPSED_ICON_CONTAINER_HEIGHT = 64; // px

/**
 * SIDEBAR_DEFAULT_OPEN
 *
 * Controls whether filter sidebars are open (expanded) or closed (collapsed)
 * by default when a page first loads.
 *
 * - `false` (current default): sidebars start collapsed — users expand them on demand.
 * - `true`: sidebars start open — the legacy behavior before the minimise-by-default change.
 *
 * Changing this single value toggles the default across ALL filter sidebars in
 * the application. Every page component and feature hook that owns a filter
 * sidebar open/close state should initialise with this constant:
 *
 *   const [isSidebarOpen, setSidebarOpen] = useState(SIDEBAR_DEFAULT_OPEN);
 *   const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(SIDEBAR_DEFAULT_OPEN);
 *
 * RunStatusPage also uses this constant (no exception). The earlier requirement
 * for `useState(true)` was tied to an auto-apply-on-mount side-effect in
 * `RunStatusFilters` that forced the sidebar open so the component would mount
 * and seed the initial query. That dependency was removed by the query-seeding
 * rework: the initial query plan is now built at the page level from URL params
 * and runtime config (`buildRunStatusQueryPlan` / `paramsToFilter`), so
 * `RunStatusFilters` no longer needs to be mounted immediately on page load.
 */
export const SIDEBAR_DEFAULT_OPEN = false;